import {
  getMessaging,
  getToken,
  deleteToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import { arrayRemove, arrayUnion, doc, setDoc } from "firebase/firestore";
import { COL_PIPELINE_USERS } from "@closing/shared";
import { app, auth, db } from "./firebase";

// Web Push certificate public key (Firebase console → Cloud Messaging).
// Same key the CRM uses — one Firebase project, one key pair.
export const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

export const pushSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;

// App-level opt-out: "Disable on this device" can't revoke the browser
// permission, so without this flag the next login's token refresh would
// silently re-subscribe the device.
const OPTOUT_KEY = "closing-push-optout";

export const isPushOptedOut = (): boolean => localStorage.getItem(OPTOUT_KEY) === "1";

export const isPushEnabled = (): boolean =>
  pushSupported() && Notification.permission === "granted" && !isPushOptedOut();

export function getPushPermission(): NotificationPermission | "unsupported" {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

async function messagingIfSupported() {
  return (await isSupported()) ? getMessaging(app) : null;
}

async function registerMessagingSW(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/firebase-messaging-sw.js");
}

async function saveToken(token: string): Promise<void> {
  const u = auth.currentUser;
  if (!u) return;
  // Tokens live on our pipeline-users doc — the same place the CRM's
  // notification function (and ours) reads them from.
  await setDoc(
    doc(db, COL_PIPELINE_USERS, u.uid),
    {
      // Our tokens live in closingFcmTokens — separate from the CRM's
      // fcmTokens pool, since tokens are origin-bound and a closing
      // notification sent to a CRM token renders under Deal Pipeline's
      // branding. The arrayRemove cleans this token out of the shared pool
      // if it landed there under the old code.
      closingFcmTokens: arrayUnion(token),
      fcmTokens: arrayRemove(token),
    },
    { merge: true },
  );
}

export async function subscribeToPush(): Promise<{ ok: boolean; error?: string }> {
  if (!pushSupported()) return { ok: false, error: "Notifications not supported on this browser" };
  if (!vapidKey) return { ok: false, error: "Push not configured (missing VAPID key)" };
  if (!auth.currentUser) return { ok: false, error: "Not signed in" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return {
      ok: false,
      error:
        permission === "denied"
          ? "Notifications blocked — enable them in browser settings."
          : "Permission not granted",
    };
  }

  const messaging = await messagingIfSupported();
  if (!messaging) return { ok: false, error: "Messaging not supported on this browser" };

  try {
    const swReg = await registerMessagingSW();
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
    if (!token) return { ok: false, error: "Could not obtain push token" };
    await saveToken(token);
    localStorage.removeItem(OPTOUT_KEY);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Subscription failed" };
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  localStorage.setItem(OPTOUT_KEY, "1");
  const messaging = await messagingIfSupported();
  const u = auth.currentUser;
  if (!messaging || !u) return;
  try {
    const token = await getToken(messaging, { vapidKey }).catch(() => null);
    if (token) {
      await deleteToken(messaging).catch(() => {});
      await setDoc(
        doc(db, COL_PIPELINE_USERS, u.uid),
        { closingFcmTokens: arrayRemove(token), fcmTokens: arrayRemove(token) },
        { merge: true },
      );
    }
  } catch {
    // best-effort
  }
}

/**
 * Re-save the token on login when permission is already granted — unless the
 * user opted this device out via Settings.
 */
export async function refreshPushTokenIfGranted(): Promise<void> {
  if (!pushSupported() || !vapidKey || !auth.currentUser) return;
  if (Notification.permission !== "granted" || isPushOptedOut()) return;
  const messaging = await messagingIfSupported();
  if (!messaging) return;
  try {
    const swReg = await registerMessagingSW();
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
    if (token) await saveToken(token);
  } catch (err) {
    console.warn("[push] token refresh failed:", err);
  }
}

/**
 * Foreground FCM messages are delivered to the page, not the service worker
 * — without this they'd be silently dropped while the app is focused. The
 * caller renders them as in-app toasts. Returns an unsubscribe.
 */
export async function onForegroundMessage(
  handler: (data: { title: string; body: string; url: string }) => void,
): Promise<() => void> {
  const messaging = await messagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, (payload: MessagePayload) => {
    const d = payload.data ?? {};
    handler({
      title: d.title ?? "Closing Checklists",
      body: d.body ?? "",
      url: d.url ?? "/",
    });
  });
}
